import { useCallback, useEffect, useState } from 'react';
import {
  Banner,
  Button,
  Card,
  EmptyState,
  RewardPill,
  StatusPill,
  Table,
  TCell,
  THead,
  TRow,
} from '@cou/admin-ui';
import { defaultAwardsClient, type AdminAward, type AwardsClient } from '../awards/awards-client';
import { useAdminAuth } from '../session/admin-auth-context';
import { toRewardType } from '../prizes/reward-cell';
import { awardPillState } from '../awards/award-state';

/**
 * The contest detail's Awards tab (API-23, admin-ui.md → Contest detail › Awards): this Contest's
 * reward delivery, with Retry on a Failed award (GL-13). The same rows the global Awards section
 * serves, scoped to one Contest — one endpoint, one row shape, so the two cannot disagree.
 */

export interface ContestAwardsTabProps {
  readonly contestId: string;
  /** Injected for tests; defaults to the real API-23 client. */
  readonly client?: AwardsClient;
  /** Whether the operator holds `awards:retry` (admin-ui.md matrix). */
  readonly canRetry: boolean;
}

export function ContestAwardsTab({
  contestId,
  client = defaultAwardsClient,
  canRetry,
}: ContestAwardsTabProps) {
  const { getToken } = useAdminAuth();
  const [awards, setAwards] = useState<AdminAward[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const token = getToken();
    if (token === null) return;
    const result = await client.listAwards(token, { contestId });
    if (result.ok) {
      setAwards(result.awards);
      setError(null);
    } else {
      setError(result.message ?? 'The awards lookup failed.');
      setAwards(null);
    }
  }, [client, contestId, getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onRetry(award: AdminAward) {
    const token = getToken();
    if (token === null) return;
    const result = await client.retryAward(token, award.id);
    if (result.ok) {
      setAwards((current) =>
        current === null ? current : current.map((a) => (a.id === award.id ? result.award : a)),
      );
      setError(null);
    } else {
      // A 409 means the pump claimed it, or another operator got there first — say so rather than
      // leaving the row looking untouched.
      setError(result.message ?? 'The retry failed.');
    }
  }

  return (
    <Card title="Awards">
      {error !== null && (
        <Banner data-testid="contest-awards-error" variant="danger" className="mb-3">
          {error}
        </Banner>
      )}

      {awards !== null && awards.length === 0 && (
        <EmptyState
          data-testid="contest-awards-empty"
          variant="section"
          icon={<AwardIcon />}
          title="No awards yet"
          body="Awards are generated for winning entries after the contest settles."
        />
      )}

      {awards !== null && awards.length > 0 && (
        <Table columns="minmax(0,1.1fr) 90px 110px minmax(0,1fr) 120px 90px minmax(0,1.3fr) 90px">
          <THead>
            <TCell>Account ref</TCell>
            <TCell>Tier</TCell>
            <TCell>Segment</TCell>
            <TCell>Reward</TCell>
            <TCell>State</TCell>
            <TCell>Attempts</TCell>
            <TCell>Last error</TCell>
            <TCell />
          </THead>
          {awards.map((award) => (
            <TRow key={award.id} data-testid={`contest-award-${award.id}`}>
              <TCell variant="mono">{award.account}</TCell>
              <TCell variant="num">{award.tier ?? '—'}</TCell>
              {/* The payout column this Award was built and paid against (GL-12), by its registry
                  label so it reconciles with the entry's snapshot; the raw ref stands in when the
                  registry has no label, never a blank (CAE-559). */}
              <TCell className="truncate">{award.segmentLabel ?? award.segment ?? 'default'}</TCell>
              <TCell>
                <RewardPill
                  type={toRewardType(award.rewardType) ?? 'freebet'}
                  label={award.displayValue}
                />
              </TCell>
              <TCell>
                <StatusPill kind="award" state={awardPillState(award.status)} />
              </TCell>
              <TCell variant="num">{award.attempts}</TCell>
              <TCell className="text-danger">{award.lastError ?? '—'}</TCell>
              <TCell>
                {/* Only Failed is stuck awaiting a human (GL-13); Delivered would double-credit. */}
                {award.status === 'Failed' && canRetry && (
                  <Button variant="outline" size="sm" onClick={() => void onRetry(award)}>
                    Retry
                  </Button>
                )}
              </TCell>
            </TRow>
          ))}
        </Table>
      )}

      {awards !== null && awards.length > 0 && (
        <p className="mt-2 text-xs text-ink-muted">
          Delivered {formatDeliveredAt(awards)} of {awards.length} on this page.
        </p>
      )}
    </Card>
  );
}

/** How many of the loaded Awards have actually reached the player's account. */
function formatDeliveredAt(awards: readonly AdminAward[]): number {
  return awards.filter((award) => award.status === 'Delivered').length;
}

function AwardIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="9" r="5" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M9 13l-2 7 5-3 5 3-2-7"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}
